import {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef,
  useSyncExternalStore,
} from 'react';
import { Title, usePermissions } from 'react-admin';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid2 as Grid,
  Button,
  TextField,
  MenuItem,
  Alert,
  Chip,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Tooltip,
  IconButton,
  Skeleton,
  Divider,
} from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import {
  getIdToken,
  getSessionGeneration,
  getSessionRevision,
  subscribeSession,
} from '../providers/cognito';
import { apiFetch } from '../providers/api-rest-provider';
import { useIntegrationSession } from '../pages/integrations/useIntegrationSession';
import type { Permissions } from '../types';
import { can } from '../utils/capability-helpers';
import { isValidAgencyAccountId } from './data-exchange-helpers';
import {
  CredentialStatus,
  type CredentialWorkflowMetadata,
} from './data-exchange-credentials';
import {
  clearCredentialOperationKey,
  credentialOperationKeyForIntent,
  type CredentialOperationKeyState,
} from './data-exchange-credential-helpers';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface DatasetEntry {
  name: string;
  entityType: string;
  description: string;
  schema: Record<string, unknown>;
}

interface ManifestEntry {
  dataset: string;
  export_date: string;
  record_count: number;
  generated_at: string;
  stale: boolean;
  files: { name: string; sha256: string }[];
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isDemoMode = () =>
  import.meta.env.VITE_DEMO_MODE === 'true' ||
  new URLSearchParams(window.location.search).get('demo') === 'true';

interface SessionBinding {
  key: string;
  revision: number;
  generation: number;
}

const sessionSnapshot = (): string => `${getSessionRevision()}:${getSessionGeneration()}`;

function useSessionBinding(): SessionBinding {
  const key = useSyncExternalStore(subscribeSession, sessionSnapshot, sessionSnapshot);
  const revision = getSessionRevision();
  const generation = getSessionGeneration();
  return useMemo(() => ({ key, revision, generation }), [key, revision, generation]);
}

const isSessionBindingCurrent = (binding: SessionBinding): boolean =>
  binding.revision === getSessionRevision() &&
  binding.generation === getSessionGeneration();

async function apiJson<T>(
  path: string,
  binding: SessionBinding,
  options: RequestInit,
  pinnedToken?: string,
): Promise<T> {
  const value = (await apiFetch(path.replace(/^\/+/, ''), options, pinnedToken)) as T;
  if (!isSessionBindingCurrent(binding)) {
    throw new Error('Session changed; response discarded');
  }
  return value;
}

function abortableDemoDelay(signal: AbortSignal): Promise<void> {
  if (signal.aborted) {
    return Promise.reject(new DOMException('Request aborted', 'AbortError'));
  }
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      window.clearTimeout(timer);
      reject(new DOMException('Request aborted', 'AbortError'));
    };
    const timer = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, 600);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

interface SessionOwnedState<T> {
  owner: string;
  loading: boolean;
  data: T;
  error: string | null;
}

// ---------------------------------------------------------------------------
// Demo fixtures
// ---------------------------------------------------------------------------

const DEMO_DATASETS: DatasetEntry[] = [
  {
    name: 'students',
    entityType: 'STUDENT',
    description: 'Student records',
    schema: { type: 'object' },
  },
  {
    name: 'attempts',
    entityType: 'ATTEMPT',
    description: 'Assessment attempts',
    schema: { type: 'object' },
  },
  {
    name: 'courses',
    entityType: 'COURSE',
    description: 'Course catalog',
    schema: { type: 'object' },
  },
  {
    name: 'instances',
    entityType: 'INSTANCE',
    description: 'Course instances',
    schema: { type: 'object' },
  },
];

const DEMO_MANIFESTS: ManifestEntry[] = [
  {
    dataset: 'students',
    export_date: '2026-07-07',
    record_count: 142,
    generated_at: '2026-07-07T04:12:00Z',
    stale: false,
    files: [{ name: 'data-0000.jsonl.gz', sha256: 'abc123' }],
  },
  {
    dataset: 'attempts',
    export_date: '2026-07-07',
    record_count: 317,
    generated_at: '2026-07-07T04:14:00Z',
    stale: false,
    files: [{ name: 'data-0000.jsonl.gz', sha256: 'def456' }],
  },
  {
    dataset: 'courses',
    export_date: '2026-07-06',
    record_count: 8,
    generated_at: '2026-07-06T04:11:00Z',
    stale: true,
    files: [{ name: 'data-0000.jsonl.gz', sha256: 'ghi789' }],
  },
];

// ---------------------------------------------------------------------------
// Credentials issuance card
// ---------------------------------------------------------------------------

export function CredentialsCard() {
  const session = useSessionBinding();
  const [agencyAccountId, setAgencyAccountId] = useState('');
  const [reason, setReason] = useState('');
  const [requestState, setRequestState] = useState<
    SessionOwnedState<CredentialWorkflowMetadata | null>
  >({ owner: session.key, loading: false, data: null, error: null });
  const [confirmRotate, setConfirmRotate] = useState(false);
  const operationKey = useRef<CredentialOperationKeyState>({});
  const activeRequest = useRef<{ id: number; controller: AbortController } | null>(null);
  const nextRequestId = useRef(0);
  const normalizedAccountId = agencyAccountId.trim();
  const accountIdValid = isValidAgencyAccountId(normalizedAccountId);
  const normalizedReason = reason.trim();
  const reasonValid = normalizedReason.length >= 10 && normalizedReason.length <= 500;
  const visibleState =
    requestState.owner === session.key
      ? requestState
      : { owner: session.key, loading: false, data: null, error: null };
  const { loading, data: result, error } = visibleState;

  useEffect(() => {
    activeRequest.current?.controller.abort();
    activeRequest.current = null;
    setRequestState({ owner: session.key, loading: false, data: null, error: null });
    setConfirmRotate(false);
    return () => {
      activeRequest.current?.controller.abort();
      activeRequest.current = null;
    };
  }, [session.key]);

  const issue = async (regenerate = false) => {
    if (!accountIdValid) {
      setRequestState({
        owner: session.key,
        loading: false,
        data: null,
        error: 'Agency AWS Account ID must be exactly 12 digits.',
      });
      return;
    }
    if (!reasonValid) {
      setRequestState({
        owner: session.key,
        loading: false,
        data: null,
        error: 'Operational reason must contain 10 to 500 characters.',
      });
      return;
    }
    const binding = session;
    const controller = new AbortController();
    const requestId = ++nextRequestId.current;
    activeRequest.current?.controller.abort();
    activeRequest.current = { id: requestId, controller };
    setRequestState({ owner: binding.key, loading: true, data: null, error: null });
    const intent = JSON.stringify([
      binding.revision,
      normalizedAccountId,
      normalizedReason,
      regenerate,
    ]);
    const idempotencyKey = credentialOperationKeyForIntent(operationKey.current, intent);
    try {
      let response: CredentialWorkflowMetadata;
      if (isDemoMode()) {
        await abortableDemoDelay(controller.signal);
        response = {
          request_id: 'a'.repeat(64),
          operation_id: 'a'.repeat(64),
          tenant_id: 'AETC',
          agency_account_id: normalizedAccountId,
          state: regenerate ? 'PENDING_HANDOFF' : 'AWAITING_ACKNOWLEDGMENT',
          role_arn: 'arn:aws-us-gov:iam::123456789012:role/data-exchange-aetc-reader',
          created_at: new Date().toISOString(),
          updated_at: new Date().toISOString(),
        };
      } else {
        const pinnedToken = getIdToken();
        if (!pinnedToken) {
          throw new Error('Your authenticated session is unavailable. Sign in again.');
        }
        response = await apiJson<CredentialWorkflowMetadata>(
          'data-exchange/credential-requests',
          binding,
          {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            signal: controller.signal,
            body: JSON.stringify({
              agency_account_id: normalizedAccountId,
              regenerate,
              reason: normalizedReason,
            }),
          },
          pinnedToken,
        );
      }
      if (
        controller.signal.aborted ||
        activeRequest.current?.id !== requestId ||
        !isSessionBindingCurrent(binding)
      )
        return;
      setRequestState({
        owner: binding.key,
        loading: false,
        data: response,
        error: null,
      });
      clearCredentialOperationKey(operationKey.current, intent);
    } catch (e) {
      if (
        controller.signal.aborted ||
        activeRequest.current?.id !== requestId ||
        !isSessionBindingCurrent(binding)
      )
        return;
      setRequestState({
        owner: binding.key,
        loading: false,
        data: null,
        error: e instanceof Error ? e.message : 'Request failed',
      });
    } finally {
      if (activeRequest.current?.id === requestId && isSessionBindingCurrent(binding)) {
        activeRequest.current = null;
        setRequestState((current) =>
          current.owner === binding.key ? { ...current, loading: false } : current,
        );
        setConfirmRotate(false);
      }
    }
  };

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Credentials Issuance
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Generate a cross-account IAM role for your agency to pull data from the
          Orchestra Data Exchange. The External ID is stored securely in AWS Secrets
          Manager and shared via a secure channel — it is never displayed here.
        </Typography>

        <Grid container spacing={2} alignItems="flex-start" mb={2}>
          <Grid size={{ xs: 12, sm: 7 }}>
            <TextField
              fullWidth
              size="small"
              label="Agency AWS Account ID"
              placeholder="123456789012"
              value={agencyAccountId}
              onChange={(e) => setAgencyAccountId(e.target.value)}
              disabled={loading}
              error={agencyAccountId.length > 0 && !accountIdValid}
              helperText={
                agencyAccountId.length > 0 && !accountIdValid
                  ? 'Enter exactly 12 digits.'
                  : ' '
              }
              inputProps={{ inputMode: 'numeric', maxLength: 12 }}
            />
          </Grid>
          <Grid size={{ xs: 12 }}>
            <TextField
              fullWidth
              size="small"
              multiline
              minRows={2}
              label="Operational reason"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={loading}
              error={reason.length > 0 && !reasonValid}
              helperText="Required for the durable privileged-operation audit record (10–500 characters)."
              inputProps={{ maxLength: 500 }}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 3 }}>
            <Button
              fullWidth
              variant="contained"
              onClick={() => issue(false)}
              disabled={loading || !accountIdValid || !reasonValid}
            >
              {loading ? <CircularProgress size={20} /> : 'Issue Credentials'}
            </Button>
          </Grid>
          <Grid size={{ xs: 12, sm: 2 }}>
            <Tooltip title="Start a staged External ID rotation">
              <Button
                fullWidth
                variant="outlined"
                color="warning"
                startIcon={<RefreshIcon />}
                onClick={() => setConfirmRotate(true)}
                disabled={loading || !accountIdValid || !reasonValid}
              >
                Rotate
              </Button>
            </Tooltip>
          </Grid>
        </Grid>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {result && <CredentialStatus metadata={result} />}
      </CardContent>

      {/* Rotation confirm dialog */}
      <Dialog open={confirmRotate} onClose={() => setConfirmRotate(false)}>
        <DialogTitle>Rotate External ID?</DialogTitle>
        <DialogContent>
          <Typography>
            This starts a staged rotation. The current External ID remains valid during
            the bounded handoff window. An authorized custodian must deliver the pending
            credential and record recipient role-assumption proof before activation.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmRotate(false)}>Cancel</Button>
          <Button
            color="warning"
            variant="contained"
            onClick={() => issue(true)}
            disabled={loading}
          >
            {loading ? <CircularProgress size={18} /> : 'Rotate'}
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Dataset catalog
// ---------------------------------------------------------------------------

export function DatasetCatalog() {
  const session = useSessionBinding();
  const [state, setState] = useState<SessionOwnedState<DatasetEntry[]>>({
    owner: session.key,
    loading: true,
    data: [],
    error: null,
  });
  const visibleState =
    state.owner === session.key
      ? state
      : { owner: session.key, loading: true, data: [], error: null };
  const { data: datasets, loading, error } = visibleState;

  useEffect(() => {
    const binding = session;
    const controller = new AbortController();
    let active = true;
    setState({ owner: binding.key, loading: true, data: [], error: null });
    queueMicrotask(() => {
      const load = async () => {
        try {
          let data: DatasetEntry[];
          if (isDemoMode()) {
            data = DEMO_DATASETS;
          } else {
            data = await apiJson<DatasetEntry[]>('data-exchange/datasets', binding, {
              signal: controller.signal,
            });
          }
          if (!Array.isArray(data)) {
            throw new Error('Dataset response has an invalid shape.');
          }
          if (!active || controller.signal.aborted || !isSessionBindingCurrent(binding)) {
            return;
          }
          setState({
            owner: binding.key,
            loading: false,
            data,
            error: null,
          });
        } catch (loadError) {
          if (!active || controller.signal.aborted || !isSessionBindingCurrent(binding)) {
            return;
          }
          setState({
            owner: binding.key,
            loading: false,
            data: [],
            error:
              loadError instanceof Error ? loadError.message : 'Unable to load datasets.',
          });
        }
      };
      void load();
    });
    return () => {
      active = false;
      controller.abort();
    };
  }, [session]);

  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Dataset Catalog
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          All datasets available in the daily export. Click a dataset to view its JSON
          schema.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading
          ? [...Array(4)].map((_, i) => (
              <Skeleton
                key={i}
                variant="rectangular"
                height={48}
                sx={{ mb: 1, borderRadius: 1 }}
              />
            ))
          : datasets.map((ds) => (
              <Accordion
                key={ds.name}
                disableGutters
                elevation={0}
                sx={{
                  border: '1px solid',
                  borderColor: 'divider',
                  mb: 1,
                  borderRadius: 1,
                }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box display="flex" alignItems="center" gap={2} width="100%">
                    <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                      {ds.name}
                    </Typography>
                    <Chip label={ds.entityType} size="small" variant="outlined" />
                    <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                      {ds.description}
                    </Typography>
                  </Box>
                </AccordionSummary>
                <AccordionDetails
                  sx={{
                    bgcolor: 'grey.50',
                    borderTop: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="caption"
                    component="pre"
                    sx={{
                      fontFamily: 'monospace',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-word',
                      fontSize: 11,
                    }}
                  >
                    {JSON.stringify(ds.schema, null, 2)}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Export health table
// ---------------------------------------------------------------------------

export function ExportHealthTable({ dataset }: { dataset: string }) {
  const session = useSessionBinding();
  const selectionKey = JSON.stringify([session.key, dataset]);
  const [state, setState] = useState<SessionOwnedState<ManifestEntry[]>>({
    owner: selectionKey,
    loading: true,
    data: [],
    error: null,
  });
  const activeRequest = useRef<{ id: number; controller: AbortController } | null>(null);
  const nextRequestId = useRef(0);
  const visibleState =
    state.owner === selectionKey
      ? state
      : { owner: selectionKey, loading: true, data: [], error: null };
  const { data: manifests, loading, error } = visibleState;

  const load = useCallback(() => {
    const binding = session;
    const requestOwner = JSON.stringify([binding.key, dataset]);
    const requestId = ++nextRequestId.current;
    const controller = new AbortController();
    activeRequest.current?.controller.abort();
    activeRequest.current = { id: requestId, controller };
    setState({ owner: requestOwner, loading: true, data: [], error: null });
    queueMicrotask(() => {
      const run = async () => {
        try {
          let data: ManifestEntry[];
          if (isDemoMode()) {
            data = DEMO_MANIFESTS.filter((m) => !dataset || m.dataset === dataset);
          } else {
            const params = new URLSearchParams();
            if (dataset) params.set('dataset', dataset);
            const suffix = params.size > 0 ? `?${params}` : '';
            data = await apiJson<ManifestEntry[]>(
              `data-exchange/manifests${suffix}`,
              binding,
              { signal: controller.signal },
            );
          }
          if (!Array.isArray(data)) {
            throw new Error('Manifest response has an invalid shape.');
          }
          if (
            controller.signal.aborted ||
            activeRequest.current?.id !== requestId ||
            !isSessionBindingCurrent(binding)
          )
            return;
          setState({
            owner: requestOwner,
            loading: false,
            data,
            error: null,
          });
        } catch (loadError) {
          if (
            controller.signal.aborted ||
            activeRequest.current?.id !== requestId ||
            !isSessionBindingCurrent(binding)
          )
            return;
          setState({
            owner: requestOwner,
            loading: false,
            data: [],
            error:
              loadError instanceof Error
                ? loadError.message
                : 'Unable to load manifests.',
          });
        } finally {
          if (activeRequest.current?.id === requestId) activeRequest.current = null;
        }
      };
      void run();
    });
  }, [dataset, session]);

  useEffect(() => {
    load();
    return () => {
      activeRequest.current?.controller.abort();
      activeRequest.current = null;
      nextRequestId.current += 1;
    };
  }, [load]);

  const staleCount = manifests.filter((m) => m.stale).length;

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Export Health
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            {staleCount > 0 && (
              <Alert
                severity="warning"
                icon={<WarningAmberIcon fontSize="small" />}
                sx={{ py: 0, px: 1 }}
              >
                <Typography variant="caption">{staleCount} stale</Typography>
              </Alert>
            )}
            <IconButton size="small" onClick={load}>
              <RefreshIcon fontSize="small" />
            </IconButton>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Latest export manifest per dataset. "Stale" = generated more than 26 hours ago.
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Skeleton variant="rectangular" height={120} />
        ) : manifests.length === 0 ? (
          <Typography color="text.secondary" variant="body2">
            No export manifests found.
          </Typography>
        ) : (
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>Dataset</TableCell>
                <TableCell>Export Date</TableCell>
                <TableCell align="right">Records</TableCell>
                <TableCell>Generated At</TableCell>
                <TableCell align="center">Status</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {manifests.map((m) => (
                <TableRow key={`${m.dataset}-${m.export_date}`} hover>
                  <TableCell>
                    <Typography variant="body2" fontWeight={500}>
                      {m.dataset}
                    </Typography>
                  </TableCell>
                  <TableCell>{m.export_date}</TableCell>
                  <TableCell align="right">{m.record_count.toLocaleString()}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {new Date(m.generated_at).toLocaleString()}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    {m.stale ? (
                      <Chip
                        label="Stale"
                        color="warning"
                        size="small"
                        icon={<WarningAmberIcon fontSize="small" />}
                      />
                    ) : (
                      <Chip label="Fresh" color="success" size="small" />
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}

function DatasetCatalogList({ datasets, loading, error }: { datasets: DatasetEntry[]; loading: boolean; error: string | null }) {
  return (
    <Card>
      <CardContent>
        <Typography variant="subtitle1" fontWeight={600} gutterBottom>
          Dataset Catalog
        </Typography>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Currently advertised datasets for this session. Click a dataset to view its JSON schema; select one to request export health.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading
          ? [...Array(4)].map((_, i) => <Skeleton key={i} variant="rectangular" height={48} sx={{ mb: 1, borderRadius: 1 }} />)
          : datasets.map((ds) => (
            <Accordion key={ds.name} disableGutters elevation={0} sx={{ border: '1px solid', borderColor: 'divider', mb: 1, borderRadius: 1 }}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Box display="flex" alignItems="center" gap={2} width="100%">
                  <Typography variant="body2" fontWeight={600} sx={{ minWidth: 140 }}>
                    {ds.name}
                  </Typography>
                  <Chip label={ds.entityType} size="small" variant="outlined" />
                  <Typography variant="body2" color="text.secondary" sx={{ flex: 1 }}>
                    {ds.description}
                  </Typography>
                </Box>
              </AccordionSummary>
              <AccordionDetails sx={{ bgcolor: 'grey.50', borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography variant="caption" component="pre" sx={{ fontFamily: 'monospace', whiteSpace: 'pre-wrap', wordBreak: 'break-word', fontSize: 11 }}>
                  {JSON.stringify(ds.schema, null, 2)}
                </Typography>
              </AccordionDetails>
            </Accordion>
          ))}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Export health table
// ---------------------------------------------------------------------------

function SelectedExportHealth({ dataset, token, current }: { dataset: string; token: string; current: () => boolean }) {
  const [manifests, setManifests] = useState<ManifestEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [reload, setReload] = useState(0);
  const currentRef = useRef(current); currentRef.current = current;
  useEffect(() => {
    let active = true; const controller = new AbortController();
    queueMicrotask(async () => {
      if (!active || !currentRef.current()) return;
      setManifests([]); setLoading(true); setError(null);
      try {
        const data = isDemoMode() ? DEMO_MANIFESTS.filter(m => m.dataset === dataset) :
          await apiFetch(`data-exchange/manifests?${new URLSearchParams({ dataset })}`, { signal: controller.signal }, token);
        if (!Array.isArray(data) || data.length > 30 || data.some(m => !m || m.dataset !== dataset || typeof m.export_date !== 'string' || typeof m.generated_at !== 'string' || !Number.isFinite(Date.parse(m.generated_at)) || !Number.isSafeInteger(m.record_count) || m.record_count < 0 || typeof m.stale !== 'boolean' || !Array.isArray(m.files))) throw new Error('Manifest response does not match the selected dataset.');
        if (active && currentRef.current()) setManifests(data);
      } catch (failure) {
        if (active && currentRef.current()) setError(failure instanceof Error ? failure.message : 'Unable to load manifests.');
      } finally { if (active && currentRef.current()) setLoading(false); }
    });
    return () => { active = false; controller.abort(); };
  }, [dataset, token, reload]);
  const load = () => { setManifests([]); setError(null); setLoading(true); setReload(v => v + 1); };

  const staleCount = manifests.filter((m) => m.stale).length;

  return (
    <Card>
      <CardContent>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
          <Typography variant="subtitle1" fontWeight={600}>
            Export Health
          </Typography>
          <Box display="flex" gap={1} alignItems="center">
            {staleCount > 0 && (
              <Alert severity="warning" icon={<WarningAmberIcon fontSize="small" />} sx={{ py: 0, px: 1 }}>
                <Typography variant="caption">{staleCount} stale</Typography>
              </Alert>
            )}
            <IconButton aria-label="Refresh export health" size="small" onClick={load}><RefreshIcon fontSize="small" /></IconButton>
          </Box>
        </Box>
        <Typography variant="body2" color="text.secondary" mb={2}>
          Up to 30 recent export dates for the selected dataset. "Generated At" is the source-reported export time; "Stale" means generated more than 26 hours ago.
        </Typography>

        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

        {loading
          ? <Skeleton variant="rectangular" height={120} />
          : manifests.length === 0
            ? (error ? null : <Typography color="text.secondary" variant="body2">No export manifests found.</Typography>)
            : (
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Dataset</TableCell>
                    <TableCell>Export Date</TableCell>
                    <TableCell align="right">Records</TableCell>
                    <TableCell>Generated At</TableCell>
                    <TableCell align="center">Status</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {manifests.map((m) => (
                    <TableRow key={`${m.dataset}-${m.export_date}`} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>{m.dataset}</Typography>
                      </TableCell>
                      <TableCell>{m.export_date}</TableCell>
                      <TableCell align="right">{m.record_count.toLocaleString()}</TableCell>
                      <TableCell>
                        <Typography variant="caption">
                          {new Date(m.generated_at).toLocaleString()}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {m.stale
                          ? <Chip label="Stale" color="warning" size="small" icon={<WarningAmberIcon fontSize="small" />} />
                          : <Chip label="Fresh" color="success" size="small" />}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            )}
      </CardContent>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Authorized catalog and selected manifest lifetime
// ---------------------------------------------------------------------------
const exportsOnlineSnapshot = () => navigator.onLine;
const exportsSubscribeOnline = (notify: () => void) => { window.addEventListener('online', notify); window.addEventListener('offline', notify); return () => { window.removeEventListener('online', notify); window.removeEventListener('offline', notify); }; };
function DatasetBrowser({ token, current }: { token: string; current: () => boolean }) {
  const [datasets, setDatasets] = useState<DatasetEntry[]>([]), [loading, setLoading] = useState(true), [error, setError] = useState<string | null>(null), [selected, setSelected] = useState('');
  const currentRef = useRef(current); currentRef.current = current;
  useEffect(() => {
    let active = true; const controller = new AbortController();
    void (async () => {
      try {
        const data = isDemoMode() ? DEMO_DATASETS : await apiFetch('data-exchange/datasets', { signal: controller.signal }, token);
        if (!Array.isArray(data) || data.some(d => !d || typeof d.name !== 'string' || !/^[a-z][a-z0-9_-]{0,127}$/.test(d.name) || d.name === 'disqualifications' || typeof d.entityType !== 'string' || typeof d.description !== 'string' || !d.schema || typeof d.schema !== 'object') || new Set(data.map(d => d.name)).size !== data.length) throw new Error('Dataset catalog response is invalid or contains retired data.');
        if (active && currentRef.current()) setDatasets(data);
      } catch (failure) { if (active && currentRef.current()) setError(failure instanceof Error ? failure.message : 'Dataset catalog unavailable.'); }
      finally { if (active && currentRef.current()) setLoading(false); }
    })();
    return () => { active = false; controller.abort(); };
  }, [token]);
  return <>
    <Grid size={{ xs: 12, md: 5 }}><DatasetCatalogList datasets={datasets} loading={loading} error={error} /></Grid>
    <Grid size={{ xs: 12, md: 7 }}>
      <TextField select fullWidth label="Export dataset" value={selected} disabled={loading || !!error} onChange={event => { const value = event.target.value; setSelected(currentRef.current() && datasets.some(d => d.name === value) ? value : ''); }} sx={{ mb: 2 }}>
        <MenuItem value="">Select a dataset</MenuItem>{datasets.map(d => <MenuItem key={d.name} value={d.name}>{d.name}</MenuItem>)}
      </TextField>
      {selected ? <SelectedExportHealth key={selected} dataset={selected} token={token} current={current} /> : <Alert severity="info">Select an available dataset to view its export health.</Alert>}
    </Grid>
  </>;
}
function DatasetExports() {
  const session = useIntegrationSession(), online = useSyncExternalStore(exportsSubscribeOnline, exportsOnlineSnapshot, () => false);
  const [generation, setGeneration] = useState(0);
  if (!online || !session.token || !session.context) return <Grid size={{ xs: 12 }}><Alert severity="warning">A current online session is required to read the dataset catalog and export health.</Alert></Grid>;
  return <><Grid size={{ xs: 12 }}><Button onClick={() => setGeneration(v => v + 1)}>Refresh dataset catalog</Button></Grid><DatasetBrowser key={`${session.token}:${generation}`} token={session.token} current={session.isCurrent} /></>;
}

// ---------------------------------------------------------------------------
// Main DataExchangePage
// ---------------------------------------------------------------------------

export function DataExchangePage() {
  const { permissions } = usePermissions<Permissions>();
  const canAccess = can(permissions, 'manage_integrations');

  if (!canAccess) {
    return (
      <Box p={3}>
        <Title title="Data Exchange" />
        <Alert severity="error">
          You need tenant-root Data Exchange permission to access this page.
        </Alert>
      </Box>
    );
  }

  return (
    <Box p={3}>
      <Title title="Data Exchange" />
      <Typography variant="h5" fontWeight={600} gutterBottom>
        Data Exchange
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={3}>
        Manage cross-account data access for agency integration. Provision IAM roles,
        browse available datasets, and monitor daily export health.
      </Typography>

      {isDemoMode() && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Demo mode is active. The datasets, manifests, and credential response below are
          sample data.
        </Alert>
      )}

      <Grid container spacing={3}>
        <Grid size={{ xs: 12 }}>
          <CredentialsCard />
        </Grid>
        <Grid size={{ xs: 12 }}>
          <Divider />
        </Grid>
        <DatasetExports />
      </Grid>
    </Box>
  );
}
